<template>
	<Header title="Feedback" />
	<div class="container">
		<form
			class="feedback-card"
			@submit.prevent="sendFeedback">
			<div class="feedback-card__title-row">
				<h2 class="feedback-card__title">Share your feedback</h2>
			</div>

			<p class="feedback-card__subtitle">
				Your insights help us create better experiences. Tell us what you think!
			</p>

			<label
				class="feedback-card__label"
				for="feedback"
				>Your feedback</label
			>

			<textarea
				id="feedback"
				v-model="feedback"
				class="feedback-card__textarea"
				placeholder="Tell us what's on your mind..."
				rows="6" />

			<div
				v-if="feedbackError"
				class="form-input__error-message-container">
				<small class="form-input__error-message">
					{{ feedbackError }}
				</small>
			</div>

			<Button
				class="feedback-card__submit"
				type="submit"
				:is-loading="loading"
				@click="sendFeedback">
				{{ loading ? 'Sending...' : 'Send Feedback' }}
			</Button>

			<p class="feedback-card__hint">
				Your feedback is greatly appreciated and helps us improve our service.
			</p>
			<p
				v-if="errorMsg"
				class="feedback-card__error"
				role="alert">
				{{ errorMsg }}
			</p>
		</form>
	</div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import Button from 'primevue/button'
import FeedbackApi from '@/helpers/feedback/FeedbackApi.ts'
import Header from '@/components/Header.vue'
import { useToast } from 'primevue'
import { useRouter } from 'vue-router'
const router = useRouter()
const feedback = ref('')
const loading = ref(false)
const errorMsg = ref('')
const feedbackError = ref('')

const feedbackApi = new FeedbackApi()
const toast = useToast()
async function send(message: string) {
	await feedbackApi.sendFeedback({ message })
}

// Clear error once the input satisfies min length
watch(feedback, (val) => {
	const text = val.trim()
	if (text.length >= 25) feedbackError.value = ''
})

async function sendFeedback() {
	if (loading.value) return

	const text = feedback.value.trim()

	if (!text) {
		feedbackError.value = 'The feedback field is required.'
		return
	}
	if (text.length < 25) {
		feedbackError.value = 'Your feedback must be at least 25 characters.'
		return
	}

	loading.value = true
	errorMsg.value = ''

	try {
		await send(text)
		toast.add({
			severity: 'success',
			summary: 'Feedback sent',
			detail: 'We have received your feedback, and thank you for it greatly!',
			life: 5000,
		})
		router.push({ name: 'root' })
	} catch (e) {
		errorMsg.value = 'Something went wrong. Please try again.'
	} finally {
		loading.value = false
	}
}
</script>

<style scoped lang="scss">
.container {
	padding-top: 100px;
}

.feedback-card {
	--card-bg: var(--p-surface-900);
	--border: var(--p-surface-800);
	--muted: var(--p-surface-400);
	--accent: var(--p-yellow-500);

	background: var(--card-bg);
	border: 1px solid var(--border);
	border-radius: 12px;
	padding: 16px;

	.feedback-card__title-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 8px;
	}

	.feedback-card__title {
		font-size: 20px;
		font-weight: 800;
	}

	.feedback-card__subtitle {
		color: var(--muted);
		margin-bottom: 16px;
	}

	.feedback-card__label {
		display: block;
		font-weight: 600;
		margin-bottom: 8px;
	}

	.feedback-card__textarea {
		width: 100%;
		border: 1px solid var(--border);
		border-radius: 12px;
		padding: 12px;
		background: transparent;
		color: inherit;
		resize: vertical;
		min-height: 140px;
		transition: background-color 0.15s ease;

		&::placeholder {
			color: var(--muted);
		}

		&:focus,
		&:focus-visible {
			outline: none;
			box-shadow: none;
			border-color: var(--border);
		}
	}

	.feedback-card__submit {
		width: 100%;
		margin-top: 14px;
		padding: 12px 14px;
		border-radius: 10px;
		border: 1px solid var(--border);
		background: var(--accent);
		color: #0a0a0a;
		font-weight: 800;
		cursor: pointer;
		transition:
			transform 0.06s ease,
			filter 0.15s ease,
			box-shadow 0.15s ease;

		&:hover {
			filter: brightness(1.05);
			box-shadow: 0 8px 22px rgba(238, 255, 1, 0.2);
		}

		&:active {
			transform: translateY(1px);
		}

		// Only used when loading=true
		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
			box-shadow: none;
		}
	}

	.feedback-card__hint {
		margin-top: 10px;
		color: var(--muted);
		font-size: 13px;
	}

	.feedback-card__error {
		margin-top: 12px;
		border: 1px solid rgba(239, 68, 68, 0.35);
		background: rgba(239, 68, 68, 0.08);
		color: #fecaca;
		padding: 10px 12px;
		border-radius: 10px;
	}
}

.form-input__error-message-container {
	display: flex;
	align-items: flex-start;
	gap: 5px;
	flex-direction: column;
	margin-top: 8px;

	.form-input__error-message {
		color: var(--p-red-500);
	}
}
</style>
